import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { useForm } from 'react-hook-form';
import { Col, Row } from 'react-flexbox-grid';
import { FaInfoCircle, FaExclamationTriangle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { date_formats, DATE_FORMAT_DEFAUT, LOGGER_LOG_TYPE, removeAccents, SEGMENT_FEATURES } from 'Config';
import { CompactPageLayout } from 'common/components/compactPageLayout';
import Label from 'common/components/label/Label';
import Button from 'common/components/button/Button';
import FormItem from 'common/components/formItem/FormItem';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import CustomFile from 'common/models/CustomFile';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import styles from './ImportChargeScreen.module.scss';
import SuppliersService from 'api/suppliers/SuppliersService';
import ImportChargeService from 'api/import-charges/ImportChargeService';
import Logger from 'common/services/Logger';
import ExpenseTemplatesService from 'api/expenseTemplates/ExpenseTemplatesService';
import SelectController from 'common/components/select/SelectController';
import InputError from 'common/components/inputError/InputError';
import { ImportChargeViewModel } from 'api/import-charges/models/ImportChargeViewModel';
import { ChargeValidationRow } from 'common/components/chargeValidation/models';
import { formatWarningMessages, parseWarnings } from 'common/components/chargeValidation/ChargeValidationService';
import ChargeValidationWarningList from 'common/components/chargeValidation/ChargeValidationWarningList';
import ChargeValidationJustifyModal from 'common/components/chargeValidation/ChargeValidationJustifyModal';
import { useHistory, useParams } from 'react-router-dom';
import { ImportExpenseFileDto, ImportExpenseFileItemDto } from 'api/import-charges/models/ImportExpenseFileDto';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import DateFormat from 'common/components/dateFormat/dateFormat';
import Popover from 'common/components/popover/Popover';
import DropdownItem from 'common/components/popover/PopoverItem';
import Dropdown from 'common/components/dropdown/Dropdown';
import { FaEllipsisV } from 'react-icons/fa';
import ImportChargeFormScreen from './components/form/ImportChargeFormScreen';
import Loading from 'common/services/Loading';
import { Paged } from 'api/common/types/Page';
import { ImportExpenseFileItemsSearchCriteria } from 'api/import-charges/models/ImportExpenseFileItemsSearchCriteria';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { UserProfile } from 'api/account/models/UserProfile';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import ReactTooltip from 'react-tooltip';
import Input from 'common/components/input/Input';
import { ReactSpreadsheetImport } from 'common/components/spreadsheet/ReactSpreadsheetImport';
import GenericTypesService from 'api/genericTypes/GenericTypesService';
import Utils from 'common/services/Utils';
import { ExpenseTemplatesDto } from 'api/expenseTemplates/models/ExpenseTemplatesDto';
import VehiclesService from 'api/vehicles/VehiclesService';
import { GenericType } from 'api/genericTypes/enums/GenericType';
import FileSelector from 'common/components/fileSelector/FileSelector';
import { VehicleCatalogItem } from 'api/vehicles/models/VehicleCatalogItem';
import { SelectValueGenericTypeDto } from 'api/genericTypes/models/SelectValueGenericTypeDto';
import { RowHook } from 'common/components/spreadsheet/types';

type Types = 'edit' | 'details' | 'new' | 'list';

const ImportChargeScreen: React.FC = () => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();
    const { type, itemId } = useParams<{ type: Types, itemId: string }>();
    const [expenseFile, setExpenseFile] = useState<ImportExpenseFileDto>();
    const [expenseFileItemsPage, setExpenseFileItemsPage] = useState<Paged<ImportExpenseFileItemDto> | null>(null);
    const [criteria, setCriteria] = useState<ImportExpenseFileItemsSearchCriteria>({
        itemsPerPage: 50,
        page: 1,
        orderColumn: 'line',
        orderBy: 'asc'
    });
    const [expenseFields, setExpenseFields] = useState<any>([]);
    const [supplierOptions, setSupplierOptions] = useState<SelectValueLabel[]>([]);
    const [templates, setTemplates] = useState<ExpenseTemplatesDto[]>([]);
    const [openItemEditPopover, setOpenItemEditPopover] = useState<string | null>(null);
    const [refreshList, setRefreshList] = useState<number>(0);
    const [itemToRemove, setItemToRemove] = useState<ImportExpenseFileDto | null>(null);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const formAdd = useForm<ImportChargeViewModel>({ shouldUnregister: false });
    const { setValue, register, errors } = formAdd;
    const [isDetails] = useState<boolean>(type && type == 'details' ? true : false);
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasChargesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_IMPORT_EXPENSES_WRITE']);
    const [supplierId, setSupplierId] = useState<string | undefined>(undefined);
    const [supplierName, setSupplierName] = useState<string | undefined>(undefined);
    const [openWizard, setOpenWizard] = useState<boolean>(false);
    const [refresh, setRefresh] = useState<number>(1);

    const [attachments, setAttachments] = useState<CustomFile[]>([]);

    // Task #66078: justification modal shown when the imported document has validation warnings.
    // Task #66220: a justificação passou a ser por linha (ver ChargeValidationJustifyModal).
    const [justificationModal, setJustificationModal] = useState<{
        rows: ChargeValidationRow[];
        submit: (justificationsByLine: Record<number, string>) => void;
        cancel: () => void;
    } | null>(null);
    // Task #66078/#66220: no detalhe, cada linha com avisos abre e fecha à parte — com tudo aberto o
    // cartão fica enorme num ficheiro com muitas linhas. Fechadas por omissão.
    const [expandedWarningLines, setExpandedWarningLines] = useState<Record<string, boolean>>({});

    const chargeTypesRef = useRef<SelectValueGenericTypeDto[]>([]);
    const vehicleByLabelRef = useRef<Record<string, VehicleCatalogItem>>({});

    const getData = async () => {
        try {
            Loading.show();
            const suppliers = await SuppliersService.catalog({ isCharges: true });
            setSupplierOptions((suppliers || []).map((x: SelectValueLabel) => ({ value: x.value || '', label: x.label || '' })));
            if (itemId) {
                const file = await ImportChargeService.getById(itemId);
                setExpenseFile(file);
                if (file.status === 'OK' && !isDetails) {
                    navigateTo('details', itemId);
                }
                criteria.expenseFileId = file.id;
                setSupplierId(file.supplierId);
                setSupplierName(file.supplierName);

                setAttachments(file.attachments);

                setCriteria(criteria);
                await getItems(criteria);
            } else {
                Loading.hide();
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't get import charge info`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    };

    const getItems = async (criteriap?: ImportExpenseFileItemsSearchCriteria) => {
        try {
            Loading.show();
            const data = await ImportChargeService.getListItems(criteriap ? criteriap : criteria);
            setExpenseFileItemsPage(data);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't get import expense file items`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
        finally {
            Loading.hide();
        }
    };

    const getExpensesFields = async () => {
        try {
            Loading.show();
            const [__types, __templates, __vehicles, __users] = await Promise.all([
                GenericTypesService.catalogChargeBySupplierId(supplierId!),
                ExpenseTemplatesService.listForSupplier(supplierId!),
                VehiclesService.catalogWithFeatures(),
                UsersService.getAllForCompany()
            ])

            const ordered = Utils.sortAlphabetically(__types.map(x => ({ ...x, name: x.label, label: x.isFuelType ? t(`vehicle.fuels.${x.label}` as any) : x.isSystem ? t(`common.generic_types.${x.label}` as any) : x.label })));
            setTemplates(__templates);

            chargeTypesRef.current = ordered;
            vehicleByLabelRef.current = (__vehicles ?? []).reduce<Record<string, VehicleCatalogItem>>((acc, v) => {
                if (v.label) acc[v.label] = v;
                return acc;
            }, {});

            if (__templates && __templates.length > 0) {
                const expenseTemplate = __templates[0];
                formAdd.setValue('expenseTemplateId', expenseTemplate.id);
                let fields = [];
                //let fields = [...EXPENSES_FIELDS];   

                if (expenseTemplate && expenseTemplate.jsonTemplate) {
                    fields = JSON.parse(expenseTemplate.jsonTemplate);
                }

                fields.forEach((field: any) => {
                    field.label = t(`spreadsheet_import.fields.${field.key}` as any)
                    field.example = t(`spreadsheet_import.fields.${field.key}_sample` as any)
                    field.alternateMatches = (expenseTemplate && expenseTemplate.jsonTemplate) ?
                        (expenseTemplate.alternateMatches || []).filter(x => x.header === field.key).map(x => x.alternateMatch) : [];
                    if (field.fieldType.type === 'select' && field.key === 'typeCode') {
                        field.fieldType.alternateMatches = (expenseTemplate && expenseTemplate.jsonTemplate) ? (expenseTemplate.chargeTypes || []).map(x => ({ source: x.source, target: x.fuelId ?? x.targetId })) : [];
                        field.fieldType.options = ordered;
                        field.history = true;
                    } else if (field.fieldType.type === 'select' && field.key === 'registration') {
                        const vehicles = Utils.sortAlphabetically(__vehicles.map(x => ({ ...x, value: x.label, name: x.label })));
                        field.fieldType.options = vehicles;
                        field.fieldType.alternateMatches = __vehicles.map(x => ({ source: x.label, target: x.label }));
                    } else if (field.fieldType.type === 'select' && field.key === 'responsible') {
                        const users = Utils.sortAlphabetically((__users || []).map(x => ({ value: x.id, label: x.realName, name: x.realName })));
                        field.fieldType.options = users;
                        field.fieldType.alternateMatches = users.map(x => ({ source: x.label, target: x.value }));
                    }
                    else if (field.fieldType.type === 'input' && (field.key === 'date' || field.key === 'paymentDate')) {
                        const formats = date_formats.map(x => ({ value: x, name: x, label: x }));
                        field.fieldType.options = formats;
                        field.fieldType.alternateMatches = formats.map(x => ({ source: x.value, target: x.value }));
                        //field.history = true; //para gravar historico
                        field.onlyOneOption = true;
                        field.validations = [
                            ...(field.validations ?? []),
                            {
                                'rule': 'formatdate',
                                'errorMessage': t('import_charges.error_invalid_date_format') as string,
                                'level': 'error'
                            }
                        ]
                    }

                    // mileage/quantity são validados per-row no rowHook (depende do segmento + requiredFieldsList)
                    if ((field.key === 'mileage' || field.key === 'quantity') && field.validations) {
                        field.validations = field.validations.filter((v: any) => v.rule !== 'required');
                        field.label = field.label.replace('*', '');
                    }
                });
                setExpenseFields(fields.sort((a: any, b: any) => a.label.localeCompare(b.label)));
                setRefresh(r => r + 1);
            }
            else {
                formAdd.setValue('expenseTemplateId', undefined);
                setRefresh(r => r + 1);
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't get import expense file items`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
        finally {
            Loading.hide();
        }
    }

    const resolveApplicability = (row: any) => {
        const chargeType = row.typeCode
            ? chargeTypesRef.current.find(x => x.value === row.typeCode)
            : undefined;
        const vehicle = row.registration
            ? vehicleByLabelRef.current[row.registration as string]
            : undefined;

        const requiredFields = chargeType?.requiredFieldsList ?? [];
        const segmentFeatures = vehicle?.segmentFeatures ?? [];

        return {
            vehicle,
            kmApplicable: !!(vehicle && requiredFields.includes('KMS') && segmentFeatures.includes(SEGMENT_FEATURES.KM)),
            fuelApplicable: !!(vehicle && requiredFields.includes('FUEL') && segmentFeatures.includes(SEGMENT_FEATURES.FUEL)),
        };
    };

    const validationRowHook = useCallback<RowHook<string>>((row, addError) => {
        const isEmpty = (v: any) => v === null || v === undefined || v === '';
        const { kmApplicable, fuelApplicable } = resolveApplicability(row);

        if (kmApplicable) {
            if (isEmpty(row.mileage)) {
                addError('mileage', { level: 'error', message: t('import_charges.mileage_required_for_segment') as string });
            }
        } else if (!isEmpty(row.mileage)) {
            row.mileage = '' as any;
        }

        if (fuelApplicable) {
            if (isEmpty(row.quantity)) {
                addError('quantity', { level: 'error', message: t('import_charges.quantity_required_for_segment') as string });
            }
        } else if (!isEmpty(row.quantity)) {
            row.quantity = '' as any;
        }

        return row;
    }, [t]);

    const isCellEditable = useCallback((row: any, fieldKey: string) => {
        if (fieldKey === 'mileage') return resolveApplicability(row).kmApplicable;
        if (fieldKey === 'quantity') return resolveApplicability(row).fuelApplicable;
        return true;
    }, []);

    const getCellLockedReason = useCallback((row: any, fieldKey: string): string | undefined => {
        if (fieldKey !== 'mileage' && fieldKey !== 'quantity') return undefined;

        const chargeType = row.typeCode
            ? chargeTypesRef.current.find(x => x.value === row.typeCode)
            : undefined;
        const vehicle = row.registration
            ? vehicleByLabelRef.current[row.registration as string]
            : undefined;

        if (!chargeType || !vehicle) {
            return t('import_charges.cell_locked_no_context') as string;
        }

        const requiredFields = chargeType.requiredFieldsList ?? [];
        const segmentFeatures = vehicle.segmentFeatures ?? [];

        if (fieldKey === 'mileage') {
            const segmentBlocks = !segmentFeatures.includes(SEGMENT_FEATURES.KM);
            const typeBlocks = !requiredFields.includes('KMS');
            if (segmentBlocks && typeBlocks) return t('import_charges.mileage_locked_both') as string;
            if (segmentBlocks) return t('import_charges.mileage_locked_segment') as string;
            if (typeBlocks) return t('import_charges.mileage_locked_type') as string;
        }

        if (fieldKey === 'quantity') {
            const segmentBlocks = !segmentFeatures.includes(SEGMENT_FEATURES.FUEL);
            const typeBlocks = !requiredFields.includes('FUEL');
            if (segmentBlocks && typeBlocks) return t('import_charges.quantity_locked_both') as string;
            if (segmentBlocks) return t('import_charges.quantity_locked_segment') as string;
            if (typeBlocks) return t('import_charges.quantity_locked_type') as string;
        }

        return undefined;
    }, [t]);

    // Task #66078: single source of truth for the validate-rows / import payload mapping. Reused by
    // both the inline step-4 validation (asyncRowValidation) and the submit flow so both build
    // identical items and can't drift.
    // Task #66078 (diagnóstico): loga com data/hora o que é enviado ao validate-rows e o que o
    // servidor responde, linha a linha, incluindo erros de rede. Ver na consola do browser (F12).
    const validateRowsLogged = useCallback(async (allItems: any[]) => {
        const ts = () => new Date().toISOString();
        // Task #66220: linhas com data ilegível ficam de fora. Se fossem enviadas com a data a null, o
        // bind falhava no servidor e o pedido inteiro devolvia 400 — os avisos de todas as linhas ficavam
        // congelados no que tinham antes, e a revalidação parecia não estar a acontecer.
        // Task #66695: o `hasQuantity` é só um marcador do mapeamento — a quantidade vem a 1 por defeito
        // quando o template não tem coluna de quantidade (ver buildValidationItems). Esse 1 é preciso no
        // payload da importação, mas aqui seria uma quantidade inventada a entrar nas regras de
        // capacidade, e era o que fazia o aviso dizer "1 L" numa portagem.
        const items = allItems
            .filter(i => !!i.date)
            .map(({ hasQuantity, ...i }) => (hasQuantity ? i : { ...i, qty: null }));
        // eslint-disable-next-line no-console
        console.log(`[#66078 validate-rows] ${ts()} REQUEST`, items.map(i => ({ line: i.line, registration: i.registrationNumber, qty: i.qty, kms: i.kilometers, cost: i.cost, date: i.date, isFuelType: i.isFuelType })));
        try {
            const results = await ImportChargeService.validateRows(items);
            // eslint-disable-next-line no-console
            console.log(`[#66078 validate-rows] ${ts()} RESPONSE`, results);
            (results ?? []).forEach(r => {
                // eslint-disable-next-line no-console
                console.log(`[#66078 validate-rows] ${ts()} linha ${r.line}: ${r.warnings?.length ? r.warnings.map(w => `${w.code} → ${w.message}`).join(' | ') : 'sem avisos'}`);
            });
            return results;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(`[#66078 validate-rows] ${ts()} ERRO`, error);
            throw error;
        }
    }, []);

    // Task #66220: uma data que não case com o formato escolhido dá parseToDate null — fica a null aqui
    // e é o validateRowsLogged que deixa essas linhas de fora (ver lá o porquê). Não são filtradas aqui
    // porque este mapeamento também alimenta a importação, e uma linha descartada em silêncio era pior
    // do que o pedido falhar.
    const buildValidationItems = useCallback((rows: any[]) => (rows ?? []).map((item: any, idx: number) => {
        const date = Utils.parseToDate(item.date, item.dateFormat);
        const paymentDate = item.paymentDate ? Utils.parseToDate(item.paymentDate, item.datePaymentFormat) : null;
        // Task #66695: distingue a quantidade real da que é preenchida a 1 por defeito (template sem
        // coluna de quantidade). O 1 mantém-se no payload da importação — mudá-lo alterava a quantidade
        // gravada nas despesas e os cálculos de consumo — e é o validateRowsLogged que o descarta antes
        // de validar. O marcador não chega ao servidor: é retirado nos dois sítios que enviam os items.
        const hasQuantity = !(item.quantity === '' || item.quantity == null);
        return {
            hasQuantity,
            line: idx + 1,
            date: date ? `${date}T00:00:00Z` : null,
            paymentDate: paymentDate ? `${paymentDate}T00:00:00Z` : '',
            notes: item.invoiceNumber,
            fuelCard: item.fuelCard,
            registrationNumber: item.registration,
            cost: Utils.parseNumberAndUniformize(item.value),
            qty: Utils.parseNumberAndUniformize(hasQuantity ? item.quantity : 1),
            kilometers: item.mileage === '' || item.mileage == null ? null : Utils.parseNumberAndUniformize(item.mileage),
            typeId: item.typeCode,
            isFuelType: item.isFuelType,
            responsibleName: item.responsibleOriginalName,
            responsibleId: item.responsible,
        };
    }), []);

    const onPageChange = (page: number) => {
        setCriteria({ ...criteria, page: page });
    }

    const renderActionsCell = (row: ImportExpenseFileItemDto, _: any, index: number) => {
        const elem = document.getElementById('root');
        return (
            <>
                <Popover
                    containerStyle={{ width: '500px', overflow: 'inherit', boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                    positions={['left', 'top']}
                    align={'center'}
                    reposition
                    containerParent={elem ? elem : document.getElementsByTagName('body')[0]}
                    isOpen={openItemEditPopover === row.id}
                    onChange={v => setOpenItemEditPopover(v ? row.id! : null)}
                    content={setIsPopoverOpen =>
                        <ImportChargeFormScreen
                            item={row}
                            mode={type}
                            onCancel={() => {
                                setIsPopoverOpen(false);
                            }}
                            onSave={f => {
                                setRefreshList(refreshList + 1);
                                setIsPopoverOpen(false);
                                addToast(t('common.messages.record_save_success'), { appearance: 'success' });
                            }}
                            supplierId={supplierId}
                        />
                    } />
                <div style={{ paddingRight: '1rem', position: 'relative' }}>
                    {type === 'edit' && hasChargesWritePolicy && <Dropdown
                        options={
                            <>
                                {<DropdownItem onClick={v => { setOpenItemEditPopover(v ? row.id! : null); }}>
                                    {t('common.edit')}
                                </DropdownItem>}
                            </>
                        }
                    >
                        <div>
                            <FaEllipsisV />
                        </div>
                    </Dropdown>}
                </div>
            </>
        );
    }

    const renderLineCell = (row: ImportExpenseFileItemDto) => {
        const warnings = parseWarnings(row.validationWarnings);
        return (
            <div className={styles.cellContainer}>
                <span>{row.line}</span>
                {warnings.length > 0 &&
                    <span
                        data-tip={formatWarningMessages(warnings.map(w => w.message))}
                        style={{ marginLeft: 6, color: '#E6A100', cursor: 'help' }}
                    >
                        <FaExclamationTriangle />
                        <ReactTooltip delayHide={100} scrollHide={true} />
                    </span>}
            </div>
        );
    };

    // Task #66078: card de justificação + avisos por linha; colapsa numa toggle "Avisos (N)" quando > 2.
    // Task #66220: a justificação é a da própria linha (a do documento só aparece nos imports antigos)
    // e os avisos vêm em bullets em vez de uma frase separada por ";".
    const renderJustificationCard = () => {
        const warnedItems = (expenseFileItemsPage?.items ?? []).filter(it => parseWarnings(it.validationWarnings).length > 0);
        if (warnedItems.length === 0 && !expenseFile?.justification) return <></>;
        return (
            <Row>
                <Col xs={12}>
                    <FormItem>
                        <div style={{ background: '#fef3c7', borderLeft: '4px solid #E2B434', borderRadius: 4, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div><strong>{t('import_charges.validation.accepted_with_warnings')}</strong></div>
                                {expenseFile?.justification ?
                                    <div style={{ marginTop: 4 }}><strong>{t('import_charges.validation.justification_label')}:</strong> {expenseFile.justification}</div>
                                    : <></>}
                                {warnedItems.map(it => {
                                    const isExpanded = !!expandedWarningLines[it.id];
                                    return (
                                        <div key={it.id} style={{ marginTop: 10, fontSize: '0.8rem', color: '#8a6d1b' }}>
                                            <div
                                                onClick={() => setExpandedWarningLines(prev => ({ ...prev, [it.id]: !prev[it.id] }))}
                                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, width: 'fit-content' }}
                                            >
                                                <span>
                                                    <strong>{t('import_charges.line')} {it.line}</strong>
                                                    {it.registrationNumber ? ` · ${it.registrationNumber}` : ''}
                                                </span>
                                                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                            </div>
                                            {isExpanded ?
                                                <>
                                                    <ChargeValidationWarningList rows={[{ line: it.line, warnings: parseWarnings(it.validationWarnings) }]} />
                                                    {it.justification ?
                                                        <div style={{ marginTop: 4 }}><strong>{t('import_charges.validation.justification_label')}:</strong> {it.justification}</div>
                                                        : <></>}
                                                </>
                                                : <></>}
                                        </div>
                                    );
                                })}
                            </div>
                            {(expenseFile?.userCreatedName || expenseFile?.createdDate) &&
                                <div style={{ textAlign: 'right', fontSize: '0.8rem', color: '#8a7320', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    {expenseFile?.userCreatedName ? <div>{t('import_charges.validation.accepted_by')}: <strong>{expenseFile.userCreatedName}</strong></div> : <></>}
                                    {expenseFile?.createdDate ? <div><DateFormat value={expenseFile.createdDate} format={DATE_FORMAT_DEFAUT} /></div> : <></>}
                                </div>}
                        </div>
                    </FormItem>
                </Col>
            </Row>
        );
    };

    const renderVehicleCell = (row: ImportExpenseFileItemDto) => {
        return (
            <div className={styles.cellContainer}>
                {!row.vehicleId ?
                    <div>
                        <div>
                            <span data-tip={t('import_charges.vehicle_unlinked')} className={styles.errorField}>{row.registrationNumber}</span>
                            <ReactTooltip delayHide={100} scrollHide={true} />
                        </div>
                    </div>
                    : <span>{row.registrationNumber}</span>}
            </div>
        )
    }

    const renderResponsibleCell = (row: ImportExpenseFileItemDto) => {
        return (
            <div className={styles.cellContainer}>
                {row.responsibleName && !row.responsibleId ?
                    <div>
                        <div>
                            <span data-tip={t('import_charges.responsible_unlinked')} className={styles.errorField}>{row.responsibleName}</span>
                            <ReactTooltip delayHide={100} scrollHide={true} />
                        </div>
                    </div>
                    : <span>{row.responsibleRealName || row.responsibleName || '-'}</span>}
            </div>
        )
    }

    const renderExpenseTypeCell = (row: ImportExpenseFileItemDto) => {
        return (
            <div className={styles.cellContainer}>
                {!row.typeId ?
                    <div>
                        <div>
                            <span data-tip={t('import_charges.expense_type_unlinked')} className={styles.errorField}>{row.typeCode}</span>
                            <ReactTooltip delayHide={100} scrollHide={true} />
                        </div>
                    </div>
                    : <span>{
                        row.typeCode == GenericType.FUEL && row.fuelId && row.fuelName
                            ? t('vehicle.fuels.' + row.fuelName as any)
                            : row.isSystem && row.typeCode !== GenericType.FUEL
                                ? t('common.generic_types.' + row.typeCode as any)
                                : row.typeCode
                    }
                    </span>}
            </div>
        )
    }

    const tableEditColumns: ListingTableColumn<ImportExpenseFileItemDto>[] = [
        {
            name: t('import_charges.line'),
            renderCell: renderLineCell
        },
        {
            name: t('import_charges.date'),
            renderCell: row => <div>
                <div><DateFormat value={row.date} format={DATE_FORMAT_DEFAUT} /></div>
            </div>,
            hideOn: ['sm'],
            searchField: 'date'
        },
        {
            name: t('import_charges.payment_date'),
            renderCell: row => <div>
                <div>{row.paymentDate ? <DateFormat value={row.paymentDate} format={DATE_FORMAT_DEFAUT} /> : ''}</div>
            </div>,
            hideOn: ['sm'],
            searchField: 'payment_date'
        },
        {
            name: t('import_charges.expense'),
            width: 'fill',
            renderCell: renderExpenseTypeCell
        },
        {
            name: t('import_charges.qty'),
            columnCellAlignment: 'right',
            cellAlignment: 'right',
            renderCell: row => <div><MoneyFormat value={row.qty ?? 0} suffix={''} /></div>
        },
        {
            name: t('import_charges.cost'),
            columnCellAlignment: 'right',
            cellAlignment: 'right',
            renderCell: row => <div>{row.cost !== undefined && row.cost !== null ? <MoneyFormat value={row.cost} suffix={t('common.euro')} fixedDecimalScale={true} /> : '-'}</div>
        },
        {
            name: t('import_charges.registration_number'),
            renderCell: renderVehicleCell
        },
        {
            name: t('import_charges.kilometers'),
            columnCellAlignment: 'right',
            cellAlignment: 'right',
            renderCell: row => <div> {row.kilometers != undefined && row.kilometers != null ? <MoneyFormat value={row.kilometers} suffix={t('common.kms')} /> : ''}</div>
        },
        {
            name: t('import_charges.fuel_card'),
            renderCell: row => <div>{row.fuelCard}</div>
        },
        {
            name: t('common.responsible'),
            renderCell: renderResponsibleCell,
            hideOn: ['sm']
        },
        {
            renderCell: renderActionsCell,

            width: 100,
            preventClick: true,
            cellStyle: { overflow: 'unset' },
            cellAlignment: 'right',
        }
    ];

    const tableDetailsColumns = tableEditColumns.slice(0, tableEditColumns.length - 1);

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            history.push(`/import-charges/${typeUrl}/${id}`);
        } else {
            history.push(`/import-charges`);
        }
    }


    const showRemoveItemDialog = (item: ImportExpenseFileDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }
        try {
            await ImportChargeService.remove(itemToRemove);
            setCriteria({ ...criteria, page: 1 });
            onCancelRemove();
            addToast(t('common.messages.record_delete_success'), {
                appearance: 'success',
            });
            navigateTo();
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete import charges file`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };

    // Task #66347: o wizard passou a poder arrancar sem ficheiro (grelha em branco noutros ecrãs) —
    // aqui vem sempre do upload, mas o tipo acompanha a assinatura do onSubmit.
    const submitWizard = (__expenseTemplateId: string, __supplierId: string, items: any[], file: File | undefined, matchedOptions: any[], matchedColumns: any[], chargeAttachments: CustomFile[], onSuccess: () => void, onError: () => void) => {
        const files: CustomFile[] = [];
        if (file) files.push(file as CustomFile);
        files.push(...chargeAttachments);

        Loading.show();
        ImportChargeService
            .importWizard({ expenseTemplateId: __expenseTemplateId, supplierId: __supplierId, items, matchedOptions, matchedColumns }, files)
            .then(() => {
                addToast(t('common.messages.record_save_success'), { appearance: 'success' });
                onSuccess();
                navigateTo();
                Loading.hide();
            })
            .catch(error => {
                Loading.hide();
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't import charges`, error);
                addToast(t('common.messages.record_save_error'), { appearance: 'error' });
                onError();
            })
    }


    const processFile = () => {
        if (expenseFile) {
            Loading.show();
            ImportChargeService.process({ id: expenseFile.id })
                .then(result => {
                    if (result.validated) {
                        addToast(t('common.messages.record_save_success'), { appearance: 'success' });
                        navigateTo();
                    }
                    else {
                        addToast(result.error, { appearance: 'warning' });
                    }

                })
                .catch(error => {
                    Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't import charges - process file`, error);
                    addToast(t('common.messages.record_save_error'), { appearance: 'error' });
                }).finally(() => {
                    Loading.hide();
                });
        }
    }

    useEffect(() => {
        getData();
    }, [criteria]);

    useEffect(() => {
        getItems();
    }, [refreshList]);

    useEffect(() => {
        if (supplierId) {
            getExpensesFields().catch(console.error);
        }
    }, [supplierId, userProfile?.languageCode]);




    return (
        <ScreenTitle title={t('import_charges.title')}>
            <>
                {/* new  */}
                {type == 'new' &&
                <CompactPageLayout
                    title={t('import_charges.new_title') as string}
                    onBack={() => navigateTo()}
                    contentAreaClassName={styles.contentArea}
                >
                    <div className={styles.detailsContent}>
                        <Row>
                            <Col xs={12} sm={12} lg={6} xl={4}>
                                <FormItem>
                                    <Label className={styles.label}>{t('import_charges.supplier')} *  &nbsp;
                                        <span data-tip={t('import_charges.supplier_message')}><FaInfoCircle />
                                            <ReactTooltip />
                                        </span></Label>
                                    <SelectController
                                        ref={register('supplierId')}
                                        form={formAdd}
                                        name="supplierId"
                                        placeholder={t('import_charges.supplier_select')}
                                        isDisabled={false}
                                        options={supplierOptions || []}
                                        rules={{ required: true }}
                                        filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                        onChangeSelect={(data: SelectValueLabel) => {
                                            setValue('supplierId', data ? data.value : null);
                                            setSupplierId(data.value);
                                            setRefresh(r => r + 1);
                                        }}
                                    />
                                    <InputError error={errors.supplierId} />
                                </FormItem>
                            </Col>
                            <Col xs={12} sm={12} lg={6} xl={4}>
                                <Label className={styles.label}>{t('common.attachment')} (excel) *</Label>
                                <FormItem key={`button_${refresh}}`}>
                                    <Button onClick={() => setOpenWizard(true)} text={t('import_charges.file')} isDisabled={!(formAdd.getValues('expenseTemplateId') != undefined && formAdd.getValues('supplierId') != undefined)} />
                                </FormItem>
                            </Col>
                        </Row>
                        <ReactSpreadsheetImport isOpen={openWizard} onClose={() => setOpenWizard(false)} fields={expenseFields} rowHook={validationRowHook} isCellEditable={isCellEditable} getCellLockedReason={getCellLockedReason} showRowNumber={true} asyncRowValidation={rows => validateRowsLogged(buildValidationItems(rows))} onSubmit={(data, file, onSucess, onError) => {

                            let hasError = false;

                            data.validData.forEach(item => {

                                if (item.date && Utils.parseToDate(item.date, item.dateFormat) == null) {
                                    hasError = true;
                                    addToast(t(!!item.dateFormat ? 'import_charges.error_format_date' : 'import_charges.error_format_date_without_format', { data: item.date, formato: item.dateFormat }), { appearance: 'warning' });
                                }

                                if (item.paymentDate && Utils.parseToDate(item.paymentDate, item.datePaymentFormat) == null) {
                                    hasError = true;
                                    addToast(t(!!item.datePaymentFormat ? 'import_charges.error_format_date' : 'import_charges.error_format_date_without_format', { data: item.paymentDate, formato: item.dateFormat }), { appearance: 'warning' });
                                }
                            });

                            if (hasError) return;

                            // Task #66078: same mapping the inline validation uses, so payloads never drift.
                            const items = buildValidationItems(data.validData);

                            const matchedColumns = data.matchedColumns
                                .filter(item => 'value' in item && item.value)
                                .map((item: any) => ({
                                    header: `${item.value}`,
                                    alternateMatch: item.header
                                }));

                            const history: any[] = [];
                            (expenseFields ?? []).forEach((field: any) => {
                                if (field.history) {
                                    history.push(field.key);
                                }
                            });

                            const matchedOptions = data.matchedOptions
                                .filter(item => 'column' in item && history.includes(item.column));

                            // Task #66220: cada linha com avisos leva a sua própria justificação.
                            // Task #66695: o `hasQuantity` é interno ao mapeamento e não vai para o servidor.
                            const doSubmit = (justificationsByLine: Record<number, string>) => {
                                const itemsToImport = items.map(({ hasQuantity: _hasQuantity, ...item }) => ({ ...item, justification: justificationsByLine[item.line] }));
                                submitWizard(formAdd.getValues('expenseTemplateId') ?? '', formAdd.getValues('supplierId') ?? '', itemsToImport, file, matchedOptions, matchedColumns, data.chargeAttachments ?? [], onSucess, onError);
                            };

                            // Task #66078: validate the rows server-side (kms decrease, daily-km limit,
                            // tank/battery capacity). If any warnings, require a justification per line
                            // before importing. On validation failure, don't block.
                            // Task #66695: os duplicados deixaram de ser validados.
                            Loading.show();
                            validateRowsLogged(items)
                                .then(results => {
                                    Loading.hide();
                                    const withWarnings = (results ?? []).filter(r => r.warnings && r.warnings.length > 0);
                                    if (withWarnings.length > 0) {
                                        const rows: ChargeValidationRow[] = withWarnings.map(r => {
                                            const item = items.find(i => i.line === r.line);
                                            return {
                                                line: r.line,
                                                registrationNumber: item?.registrationNumber,
                                                date: item?.date,
                                                warnings: r.warnings,
                                            };
                                        });
                                        setJustificationModal({ rows, submit: doSubmit, cancel: onError });
                                    } else {
                                        doSubmit({});
                                    }
                                })
                                .catch(error => {
                                    Loading.hide();
                                    Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't validate import charges`, error);
                                    doSubmit({});
                                });
                        }} />
                    </div>
                </CompactPageLayout>}
            {(type == 'edit' || type == 'details') &&
                <CompactPageLayout
                    title={type == 'details' ? t('import_charges.details_title') as string : t('import_charges.edit') as string}
                    onBack={() => type == 'details' ? history.goBack() : navigateTo()}
                    contentAreaClassName={styles.contentArea}
                >
                    <div className={styles.detailsContent}>
                        <Row>
                            <Col xs={12} sm={12} lg={4} xl={3}>
                                <FormItem>
                                    <Label className={styles.label}>{t('import_charges.supplier')}</Label>
                                    <Input
                                        name='supplierName'
                                        defaultValue={supplierName}
                                        disabled={true}
                                    />
                                </FormItem>
                            </Col>

                            <Col xs={12} sm={12} lg={8} xl={9}>
                                <FormItem>
                                    <Label className={styles.label}>{t('spreadsheet_import.validation_step.charges_attachments')}</Label>
                                    <FileSelector
                                        isDetails={true}
                                        isMulti={true}
                                        initialFiles={attachments}
                                        onFilesChanged={() => { return null; }}
                                        onRemoveFile={() => { return null; }}
                                    />
                                </FormItem>
                            </Col>
                        </Row>

                        {renderJustificationCard()}

                        <ListingTable
                            columns={type === 'edit' && expenseFile?.status !== 'OK' ? tableEditColumns : tableDetailsColumns}
                            rows={expenseFileItemsPage?.items ?? []}
                            allowHover={true}
                            initialSearch={{ colField: 'line', isOrderAsc: true }} />

                        <PaginationWithInfo
                            className={styles.pagination}
                            itemName={t('import_charges.list.files')}
                            currentPage={expenseFileItemsPage?.currentPage ?? 1}
                            pageItems={expenseFileItemsPage?.items.length ?? 0}
                            itemsPerPage={50}
                            totalItems={expenseFileItemsPage?.totalItems ?? 0}
                            onChange={onPageChange} />

                        <div className={styles.buttonsContainer}>
                            <Button
                                preset={'secondary'}
                                type="button"
                                onClick={() => type == 'details' ? history.goBack() : navigateTo('details', itemId)}
                                text={t('common.cancel')}
                            />
                        </div>

                        <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('common.messages.remove_record_with_ident', { name: itemToRemove?.fileName ?? '' })} />
                    </div>
                </CompactPageLayout>}

                {/* Task #66078/#66220: justificação por linha, exigida para importar um documento com avisos. */}
                <ChargeValidationJustifyModal
                    isOpen={!!justificationModal}
                    rows={justificationModal?.rows ?? []}
                    title={t('import_charges.validation.title') as string}
                    subtitle={t('import_charges.validation.subtitle') as string}
                    confirmText={t('import_charges.validation.confirm_import') as string}
                    cancelText={t('common.cancel') as string}
                    onCancel={() => { justificationModal?.cancel(); setJustificationModal(null); }}
                    onConfirm={justificationsByLine => { justificationModal?.submit(justificationsByLine); setJustificationModal(null); }}
                />
            </>
        </ScreenTitle >
    );
};

export default ImportChargeScreen; 
